import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import get from 'lodash.get';
import merge from 'lodash.merge';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Gist from 'react-gist';
import ReactPlayer from 'react-player';
//import IframeResizer from 'iframe-resizer-react';
import ResponsiveEmbed from 'react-responsive-embed';

import ContentfulImage from '../ContentfulImage';
import IframeModal from '../IframeModal';
import { ArrowLink, PrimaryLink } from '../Links';
import SmartLink from '../SmartLink';
import * as inlines from './Inlines';
import * as styles from './RichTextArticle.module.css';

const RichTextArticle = ({ copy, optionsOverride, className = '', inlineCta, customInlineCta }) => {
  const articleRef = useRef();
  const [ctasRendered, setCtasRendered] = useState(false);

  // Inject inline CTAs after rich text renderer does its thing
  useEffect(() => {
    const isCustomInlineCta = customInlineCta?.copy && customInlineCta?.link;
    if (articleRef.current && (inlineCta || isCustomInlineCta) && !ctasRendered) {
      const elements = articleRef.current.children;
      let pTags = 0;
      let cta;

      if (isCustomInlineCta) {
        cta = customInlineCta;
      } else {
        const inlineCtaOptions = {
          demo: {
            copy: 'Want a perfect website or app? FullStory can help. Request a demo today.',
            link: '/demo',
          },
          trial: {
            copy: 'Unlock every capability of FullStory for 2 weeks. Start your trial now.',
            link: '/signup',
          },
          tour: {
            copy: `Explore FullStory's key features in a guided tour. Start a free product tour now.`,
            link: 'https://app.getreprise.com/launch/5XaWRx6/',
          },
        };

        cta = inlineCtaOptions[inlineCta];
      }

      // Show CTA following 2nd <p> and then following every 8th <p> thereafter
      Object.keys(elements).forEach((el, i) => {
        if (elements[el].tagName === 'P' && elements[el].innerText !== '') {
          pTags++;
          if (pTags === 2 || pTags % 8 === 0) {
            const ctaContainer = document.createElement('div');
            const ctaComponent = (
              <ArrowLink key={`cta-${i}`} className={styles.inlineCta} url={cta.link} label={cta.copy} />
            );
            elements[el].after(ctaContainer);
            ReactDOM.render(ctaComponent, ctaContainer);
            if (pTags === 2) pTags = 9;
          }
        }
      });

      setCtasRendered(true);
    }
  }, []);

  let tableHeaders = [];
  let tableHeadersBackup = [];
  useEffect(() => {
    let tableCell = document.querySelectorAll('.rich-text-table-cell');
    tableCell.forEach((element) => {
      if (tableHeaders.length !== 0) {
        element.setAttribute('data-label', tableHeaders.shift());
      } else {
        tableHeaders.push(...tableHeadersBackup);
        element.setAttribute('data-label', tableHeaders.shift());
      }
    });
  });

  let sections = 0;
  const defaultOptions = {
    renderText: inlines.text,
    renderNode: {
      [INLINES.HYPERLINK]: inlines.hyperlink,
      [INLINES.ENTRY_HYPERLINK]: inlines.entryHyperlink,
      [BLOCKS.HEADING_1]: (node, children) => <h1 className="article-heading">{children}</h1>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { file, title, description, gatsbyImageData } = get(node, 'data.target', {});

        return (
          <figure className={styles.figure}>
            {gatsbyImageData && (
              <GatsbyImage data-ot-ignore className={styles.embeddedAsset} image={gatsbyImageData} alt={title} />
            )}
            {!gatsbyImageData && <img src={get(file, 'url')} alt={title} />}
            {description && <figcaption>{description}</figcaption>}
          </figure>
        );
      },
      [BLOCKS.TABLE]: (node, children) => (
        <table role="table" className="rich-text-table">
          {children}
        </table>
      ),
      [BLOCKS.TABLE_ROW]: (node, children) => {
        node.content.forEach((element) => {
          if (element.nodeType === 'table-header-cell') {
            tableHeaders.push(element.content[0].content[0].value);
            tableHeadersBackup.push(element.content[0].content[0].value);
          }
        });
        return (
          <tr role="row" className="rich-text-row">
            {children}
          </tr>
        );
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className="rich-text-columnheader" role="columnheader">
          {children}
        </th>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td role="cell" className="rich-text-table-cell">
          {children}
        </td>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = get(node, 'data.target.internal.type');
        // Anchor
        if (type === 'ContentfulJumpLink') {
          let id = get(node, 'data.target.sectionId');
          sections++;
          return <div data-section-num={sections} id={id} />;
        }

        if (type === 'ContentfulGithubGist') {
          // github gists
          const url = get(node, 'data.target.gistUrl', '');
          if (!url) return;
          const stripped = url.replace('https://', '').replace('http://', '').replace('.js', '');
          const split = stripped.split('/');
          const id = split[2];
          const fileSplit = id.split('#');

          return (
            <div>
              <Gist id={fileSplit[0]} />
            </div>
          );
        }
        if (type === 'ContentfulVideoEmbed') {
          // wistia embeds
          return (
            <div className={styles.playerWrapper}>
              <ReactPlayer
                className={styles.reactPlayer}
                controls={true}
                width="100%"
                height="100%"
                url={node.data.target.videoUrl}
              />
            </div>
          );
        }
        // embedded images for alignment
        if (type === 'ContentfulArticleImage') {
          const imageFile = get(node, 'data.target.image');
          const caption = get(node, 'data.target.caption');
          const url = get(node, 'data.target.url');
          if (!imageFile) {
            return;
          }
          const side = get(node, 'data.target.alignImage');
          let align = '';
          switch (side) {
            case 'Left':
              align = styles.left;
              break;
            case 'Right':
              align = styles.right;
              break;
            case 'Centered No Padding':
              align = styles.centerNoPad;
              break;
            default:
              align = styles.fullWidth;
          }
          if (!imageFile.fullWidth) {
            //let img = <img src={imageFile.file.url} alt={imageFile.description} />;
            let img = <ContentfulImage imageFragment={imageFile} gatsbyImageData={imageFile.fullWidth} />;
            if (url) {
              img = <SmartLink to={url}>{img}</SmartLink>;
            }
            return (
              <div className={[styles.fig, align].join(' ')}>
                {img}
                {caption && <figcaption>{caption}</figcaption>}
              </div>
            );
          }
          let img = <ContentfulImage imageFragment={imageFile} gatsbyImageData={imageFile.fullWidth} />;
          if (url) {
            img = <SmartLink to={url}>{img}</SmartLink>;
          }
          return (
            <div className={[styles.fig, align].join(' ')}>
              <div className={url ? styles.linkedImage : ''}>{img}</div>
              {caption && <figcaption>{caption}</figcaption>}
            </div>
          );
        }

        // embedded callout section
        if (type === 'ContentfulBlogSectionCalloutEmbed') {
          const body = get(node, 'data.target.sectionContent', '');
          const borderStyle = get(node, 'data.target.borderStyle', 'attention-left');
          const borderColor = node?.data?.target?.borderColor;
          let borderColorStyle = styles.calloutTeal;
          if (borderColor === 'Bright Blue') {
            borderColorStyle = styles.calloutBlue;
          } else if (borderColor === 'Bright Purple') {
            borderColorStyle = styles.calloutPurple;
          }
          return (
            <div
              className={[
                borderStyle === 'surround' ? styles.calloutSurround : styles.calloutLeft,
                borderColorStyle,
              ].join(' ')}
            >
              {renderRichText(body, options)}
            </div>
          );
        }

        if (type === 'ContentfulLink') {
          const label = get(node, 'data.target.linkText');
          const url = get(node, 'data.target.url');
          if (url.startsWith('https://app.getreprise.com/')) {
            const content = {
              contentType: {
                viewLabel: label,
              },
              ctaExternalLink: url,
            };
            return <IframeModal content={content} />;
          }
        }
      },
    },
  };

  const options = merge({}, defaultOptions, optionsOverride);

  return (
    <article ref={articleRef} className={[styles.postArticle, className].join(' ')}>
      {copy && renderRichText(copy, options)}
    </article>
  );
};

RichTextArticle.propTypes = {
  /** The raw json data **/
  copy: PropTypes.object.isRequired,
  optionsOverride: PropTypes.object,
};

export default RichTextArticle;
